import React, { useState, useMemo, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import CCVendorFilterBar from "./CCVendorFilters";
import CCVendorWarehouses from "./CCVendorWarehouses";
import {
  FormControl,
  Tooltip,
  Select,
  MenuItem,
  Box,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { selectComposerCartsFormatted, switchComposerItem, savePreviousState } from "../../../../features/composerSlice";
import { usePostHog } from "posthog-js/react";
import { selectStyleVendorFields } from "features/styleSlice";
import { getCanonicalSize } from "../../manual-override/utility/SizeAliases";

const getVendorData = (
  vendorCode,
  validItemInventory,
  itemStyleSku,
  masterColor
) => {
  if (!vendorCode)
    return {
      vendorInvData: [],
      availableSizes: new Set(),
      vendorSizeMap: {},
    };

  const dataKey = itemStyleSku + vendorCode + masterColor;
  const vendorData = validItemInventory[dataKey]?.data;

  if (!vendorData?.inventory_data) {
    return {
      vendorInvData: [],
      availableSizes: new Set(),
      vendorSizeMap: {},
    };
  }

  const availableSizes = new Set();
  const vendorSizeMap = {};
  vendorData.inventory_data.forEach((whse) => {
    whse[1]?.quantities.forEach((q) => {
      if (q.sizeLabel) {
        const canonical = getCanonicalSize(q.sizeLabel);
        availableSizes.add(canonical);
        // Map canonical to vendor label (first seen)
        if (!vendorSizeMap[canonical]) vendorSizeMap[canonical] = q.sizeLabel;
      }
    });
  });

  return {
    vendorInvData: vendorData.inventory_data,
    availableSizes,
    vendorSizeMap,
  };
};

const getVendorPricingData = (
  vendorCode,
  selectedSize,
  validItemInventory,
  itemStyleSku,
  masterColor
) => {
  if (!vendorCode || !selectedSize) return null;

  const dataKey = itemStyleSku + vendorCode + masterColor;
  const vendorData = validItemInventory[dataKey]?.data;

  return vendorData?.sized_pricing_data?.find(
    ([size]) => size === selectedSize
  )?.[1];
};

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  minWidth: 80,
}));

const SizeSelector = ({
  sizes,
  selectedSize,
  onSizeChange,
  disabled,
  noSizeMatch,
  vendorSizeMap,
}) => {
  if (sizes.size === 0) return null;

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "12px",
        "& .MuiTypography-root": {
          lineHeight: 1,
        },
        marginLeft: "225px",
        marginBottom: "-20px",
        position: "relative",
      }}
    >
      <Typography
        component="label"
        sx={{
          fontWeight: 500,
          display: "flex",
          alignItems: "center",
          marginTop: "5px",
        }}
      >
        Selected Size:
      </Typography>
      <Tooltip
        open={noSizeMatch}
        placement="right"
        arrow
        PopperProps={{
          sx: {
            "& .MuiTooltip-tooltip": {
              width: "220px",
              backgroundColor: "#FFA552",
              fontSize: "0.875rem",
              padding: "8px 12px",
            },
            "& .MuiTooltip-arrow": {
              color: "#FFA552",
            },
          },
        }}
        title={`Select the new vendor's correct size`}
      >
        <StyledFormControl size="small">
          <Select
            value={selectedSize || ""}
            onChange={(e) => onSizeChange(e.target.value)}
            disabled={disabled}
            sx={{
              "& .MuiSelect-select": {
                padding: "6px 14px",
                paddingBottom: "2px",
                display: "flex",
                alignItems: "center",
              },
              "& .MuiSelect-icon": {
                top: "calc(50% - 10px)",
              },
            }}
          >
            {Array.from(sizes).map((canonical) => (
              <MenuItem key={canonical} value={vendorSizeMap[canonical]}>
                {vendorSizeMap[canonical]}
              </MenuItem>
            ))}
          </Select>
        </StyledFormControl>
      </Tooltip>
    </Box>
  );
};

function CCVendorOptions({
  validItemInventory,
  validVendorCodes,
  item,
  sourceVendorCode,
  onClose,
}) {
  const dispatch = useDispatch();
  const posthog = usePostHog();
  const styleVendorFields = useSelector((state) =>
    selectStyleVendorFields(state, item.dgi_style_sku)
  );
  
  const validCarts = useSelector((state) =>
    selectComposerCartsFormatted(state, validVendorCodes)
  );

  // Filter vendors to only those that have the item available or are already in carts
  const filteredVendorCodes = useMemo(() => {
    // Start with vendors that have inventory data
    const vendorsWithInventory = new Set(validVendorCodes);
    
    // Add any vendors that already have items in carts
    Object.keys(validCarts || {}).forEach(code => vendorsWithInventory.add(code));
    
    // Always include the source vendor to allow warehouse switching
    vendorsWithInventory.add(sourceVendorCode);
    
    // Return as array (no need to filter out source vendor)
    return Array.from(vendorsWithInventory);
  }, [validVendorCodes, validCarts, sourceVendorCode]);
  
  const sortedValidCarts = useMemo(() => {
    if (!validCarts) return [];

    // Create a cart entry for every filtered vendor code, even if not in validCarts
    return filteredVendorCodes
      .map(code => ({
        code,
        totalPrice: validCarts[code]?.totalPrice || 0,
        name: validCarts[code]?.name || code
      }))
      .sort((a, b) => b.totalPrice - a.totalPrice);
  }, [validCarts, filteredVendorCodes]);

  const [selectedVendor, setSelectedVendor] = useState(
    sortedValidCarts[0]?.code || ""
  );
  const [selectedWarehouse, setSelectedWarehouse] = useState("");

  const vendorData = useMemo(() => {
    return getVendorData(
      selectedVendor,
      validItemInventory,
      item.dgi_style_sku,
      item.master_color
    );
  }, [
    selectedVendor,
    validItemInventory,
    item.dgi_style_sku,
    item.master_color,
  ]);
  const [noSizeMatch, setNoSizeMatch] = useState(false);

  const itemCanonicalSize = getCanonicalSize(item.size);

  const initialSize = useMemo(() => {
    if (vendorData.availableSizes.size === 0) return "";
    // Find canonical match
    const hasMatch = Array.from(vendorData.availableSizes).includes(itemCanonicalSize);
    return hasMatch ? vendorData.vendorSizeMap[itemCanonicalSize] : vendorData.vendorSizeMap[Array.from(vendorData.availableSizes)[0]];
  }, [vendorData.availableSizes, itemCanonicalSize]);

  useEffect(() => {
    if (vendorData.availableSizes.size === 0) {
      setNoSizeMatch(false);
      return;
    }
    const hasMatch = Array.from(vendorData.availableSizes).includes(itemCanonicalSize);
    setNoSizeMatch(!hasMatch);
  }, [vendorData.availableSizes, itemCanonicalSize]);

  const [selectedSize, setSelectedSize] = useState(initialSize);

  // Reset size when vendor changes or when available sizes change
  const derivedSelectedSize = useMemo(() => {
    if (vendorData.availableSizes.size === 0) return "";
    if (!Array.from(vendorData.availableSizes).includes(selectedSize)) {
      return initialSize;
    }
    return selectedSize;
  }, [selectedSize, vendorData.availableSizes, initialSize]);

  // Update the selected size when the derived value changes
  if (derivedSelectedSize !== selectedSize) {
    setSelectedSize(derivedSelectedSize);
  }

  const selectedPrice = useMemo(() => {
    return getVendorPricingData(
      selectedVendor,
      derivedSelectedSize,
      validItemInventory,
      item.dgi_style_sku,
      item.master_color
    );
  }, [
    selectedVendor,
    derivedSelectedSize,
    validItemInventory,
    item.dgi_style_sku,
    item.master_color,
  ]);

  const getVendorUnitPrice = (vendorCode, vendorLabelSize) => {
    const dataKey = item.dgi_style_sku + vendorCode + item.master_color;
    const pricingData = validItemInventory[dataKey]?.data?.sized_pricing_data;
    if (!pricingData) return undefined;
    // Use the canonical version of the selected size from the dropdown
    const canonical = getCanonicalSize(vendorLabelSize);
    const sizePricingDataPoint = pricingData.find(
      (size) => getCanonicalSize(size[0]) === canonical
    );
    if (sizePricingDataPoint) {
      return sizePricingDataPoint[1];
    }
    return undefined;
  };

  const handleVendorSelect = (vendorCode) => {
    if (vendorCode !== selectedVendor) {
      setSelectedVendor(vendorCode);
      setSelectedWarehouse("");
    }
  };

  const handleSwitchCart = () => {
    if (!selectedWarehouse || !selectedPrice) return;

    // Check if this is a warehouse switch within the same vendor
    const isSameVendorWarehouseSwitch = selectedVendor === sourceVendorCode;
    
    const selectedWarehouseData = vendorData.vendorInvData.find(
      ([name]) => name === selectedWarehouse
    );

    // Only proceed if we have warehouse data
    if (!selectedWarehouseData) return;
    
    // Skip if trying to use the same warehouse in the same vendor
    if (isSameVendorWarehouseSwitch && 
        selectedWarehouseData[0] === item.warehouse) {
      onClose();
      return;
    }

    const destWarehouse = {
      ...selectedWarehouseData[1],
      name: selectedWarehouseData[0],
      warehouseCode: selectedWarehouseData[1].warehouseCode,
    };

    // Get size-specific details
    const sizeInfo = destWarehouse.quantities.find(
      (q) => q.sizeLabel === derivedSelectedSize
    );

    const destStyleKey = `${selectedVendor}_style`;
    const destItem = {
      size: derivedSelectedSize,
      itemNumber: sizeInfo?.itemNumber,
      style: styleVendorFields?.[destStyleKey]
    };

    // Calculate the lowest price across all vendors for this item
    
    // 1. Parse the selected price safely - handle both string and number formats
    let selectedPriceValue;
    if (typeof selectedPrice === 'string') {
      // Remove currency symbols or non-numeric chars except decimal point
      selectedPriceValue = parseFloat(selectedPrice.replace(/[^0-9.]/g, ''));
    } else {
      selectedPriceValue = parseFloat(selectedPrice);
    }
    
    // Default to a safe value if parsing fails
    if (isNaN(selectedPriceValue)) {
      selectedPriceValue = 0;
    }
    
    // 2. Initialize lowest price to the selected price
    let lowestPrice = selectedPriceValue;
    let isLowestPrice = true;
    
    // 3. Find the lowest price among all vendors with consistent type handling
    filteredVendorCodes.forEach(vendorCode => {
      const vendorPrice = getVendorUnitPrice(vendorCode, derivedSelectedSize);
      if (vendorPrice) {
        // Handle both string and number formats consistently
        let vendorPriceValue;
        if (typeof vendorPrice === 'string') {
          vendorPriceValue = parseFloat(vendorPrice.replace(/[^0-9.]/g, ''));
        } else {
          vendorPriceValue = parseFloat(vendorPrice);
        }
        
        // Only update lowest price if parsing succeeded and value is lower
        if (!isNaN(vendorPriceValue) && vendorPriceValue < lowestPrice) {
          lowestPrice = vendorPriceValue;
        }
      }
    });
    
    // 4. Calculate if selected price is the lowest with tolerance for floating point
    isLowestPrice = selectedPriceValue <= lowestPrice || Math.abs(selectedPriceValue - lowestPrice) < 0.01;
    
    // 5. Calculate precise price difference as a number type
    let priceDifference = 0;
    if (!isLowestPrice) {
      priceDifference = parseFloat((selectedPriceValue - lowestPrice).toFixed(2));
    }

    // Save the previous state before switching cart items to enable undo functionality
    dispatch(savePreviousState());

    // Dispatch the action to switch the item
    dispatch(switchComposerItem({
      sourceVendorCode,
      sourceInvoiceNumber: item.invoice_number,
      composerItemId: item.composer_item_id,
      destVendorCode: selectedVendor,
      destItem,
      destWarehouse,
      destPrice: selectedPrice,
      // Add the new comparative price information with explicit types
      lowestPrice: isLowestPrice,
      priceDifference: priceDifference // Number with 2 decimal precision
    }));

    // Log the event with PostHog if available
    posthog?.capture("composer_switch_cart", {
      sourceVendorCode,
      sourceItemNumber: item.itemNumber,
      sourceItemQuantity: item.quantity,
      sourceWarehouseCode: item.warehouseCode,
      sourceUnitPrice: item.unitPrice,
      destinationVendorCode: selectedVendor,
      destinationItemNumber: destItem.itemNumber,
      destinationItemQuantity: item.quantity,
      destinationWarehouseCode: destWarehouse.warehouseCode,
      destinationUnitPrice: selectedPrice,
      isWarehouseSwitch: isSameVendorWarehouseSwitch,
      // Add price comparison to analytics
      isLowestPrice: isLowestPrice,
      priceDifference: priceDifference
    });

    // Close the dialog
    onClose();
  };

  // After the useState for selectedSize
  useEffect(() => {
    // When vendor changes, update selectedSize to the vendor's label for the canonical size
    if (vendorData.availableSizes.size > 0) {
      const hasMatch = Array.from(vendorData.availableSizes).includes(itemCanonicalSize);
      setSelectedSize(
        hasMatch
          ? vendorData.vendorSizeMap[itemCanonicalSize]
          : vendorData.vendorSizeMap[Array.from(vendorData.availableSizes)[0]]
      );
    }
    // eslint-disable-next-line
  }, [selectedVendor, vendorData.availableSizes, itemCanonicalSize]);

  return (
    <div className="flex flex-col p-4">
      <div className="w-full flex justify-start">
        <div className="px-[24px] mb-[20px]">
          <span className="text-sm">{`${item.style}, ${item.master_color}`}</span>
          <div className="text-wolfGrey flex items-center gap-[16px]">
            <div className="text-base">
              <span className="text-typeGrey text-base">Size:</span> {item.size}
            </div>
            <div className="text-base">
              <span className="text-typeGrey text-base">Quantity:</span>{" "}
              {item.quantity}
            </div>
          </div>
        </div>
        <SizeSelector
          sizes={vendorData.availableSizes}
          selectedSize={selectedSize}
          onSizeChange={setSelectedSize}
          disabled={!selectedVendor}
          noSizeMatch={noSizeMatch}
          vendorSizeMap={vendorData.vendorSizeMap}
        />
      </div>

      <div className="flex space-x-12 w-full">
        <CCVendorFilterBar
          sortedValidCarts={sortedValidCarts}
          selectedVendorCode={selectedVendor}
          selectedSize={selectedSize}
          getVendorUnitPrice={(vendorCode) =>
            getVendorUnitPrice(vendorCode, selectedSize)
          }
          onVendorSelect={handleVendorSelect}
        />

        <CCVendorWarehouses
          warehouseData={vendorData.vendorInvData}
          selectedSize={derivedSelectedSize}
          selectedVendorCode={selectedVendor}
          item={item}
          selectedPrice={selectedPrice}
          selectedWarehouse={selectedWarehouse}
          onWarehouseSelect={setSelectedWarehouse}
          onSwitchCart={handleSwitchCart}
          sourceVendorCode={sourceVendorCode}
        />
      </div>
    </div>
  );
}

export default CCVendorOptions;
